import { useCallback, useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";

import { PLAYLIST } from "MVVM/GraphQL/playlist";
import { getTagLabel } from "MVVM/Utilities/ClipTags";
import { IDayOption } from "./dateUtils";

export const TODAY_API_KEY_STORAGE = "allstarTodayApiKey";

const readStoredKey = () => {
  try {
    return localStorage.getItem(TODAY_API_KEY_STORAGE);
  } catch {
    return null; // storage unavailable (private mode / blocked)
  }
};

export const useTodayApiKey = (enabled = true) => {
  const [override, setOverride] = useState<string | null>(readStoredKey);

  const setApiKey = (key: string) => {
    const trimmed = key.trim();
    try {
      if (trimmed) localStorage.setItem(TODAY_API_KEY_STORAGE, trimmed);
      else localStorage.removeItem(TODAY_API_KEY_STORAGE);
    } catch {
      // storage unavailable; the in-memory override below still applies
    }
    setOverride(trimmed || null);
  };

  // Key entry is internal-only; a hand-written localStorage entry must not
  // change the key for public viewers.
  const activeOverride = enabled ? override : null;

  return {
    apiKey: activeOverride || process.env.REACT_APP_ALLSTAR_TODAY_API_KEY || "",
    isOverridden: !!activeOverride,
    setApiKey,
  };
};

export type TTodayError = "unauthorized" | "fetch" | null;

export interface ITodayClip {
  createdAt: string;
  developer?: { profile?: { internalId?: string | null } | null } | null;
  playerGameIdentifier?: string | null;
  score: number;
  thumbnailStandardUrl?: string | null;
  shareId: string;
  tags?: { key: string; value: string }[];
  title: string;
  user?: { _id: string; avatarUrl?: string; username?: string };
  username?: string;
  views?: number;
}

const PLAYLIST_LIMIT = 100;
// 7d so repeated queries for the same day reuse the cached partner playlist
const PLAYLIST_EXPIRY = "7d";

// TODO: temporary for testing — staging has no scored daily clip data, so
// route staging-configured builds at prod. Remove before release.
export const getPartnerUrl = () => {
  const configured = process.env.REACT_APP_PARTNER_URL;
  return configured === "https://staging-prt.allstar.gg"
    ? "https://prt.allstar.gg"
    : configured;
};

export const useDailyPartnerClips = ({
  day,
  apiKey,
}: {
  day: IDayOption;
  apiKey: string;
}) => {
  const [error, setError] = useState<TTodayError>(null);
  const [hydratedId, setHydratedId] = useState<string | null>(null);
  const [playlistId, setPlaylistId] = useState<string | null>(null);
  const [resolving, setResolving] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [fetchPlaylist, { data, loading }] = useLazyQuery(PLAYLIST);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);

  useEffect(() => {
    let cancelled = false;

    setError(null);
    setPlaylistId(null);

    setResolving(true);

    (async () => {
      try {
        const response = await fetch(
          `${getPartnerUrl()}/cs/clips/playlist?limit=${PLAYLIST_LIMIT}&sort=score&expiry=${PLAYLIST_EXPIRY}&startDate=${
            day.startDate
          }&endDate=${day.endDate}`,
          { headers: { "X-API-KEY": apiKey } },
        );

        if (response.status === 401 || response.status === 403) {
          if (!cancelled) {
            setError("unauthorized");
            setResolving(false);
          }
          return;
        }

        if (!response.ok) {
          if (!cancelled) {
            setError("fetch");
            setResolving(false);
          }
          return;
        }

        const json = await response.json();
        const playlistUrl = json?.data?.playlistUrl;
        const id = playlistUrl
          ? new URL(playlistUrl).searchParams.get("playlist")
          : null;

        if (cancelled) return;
        if (id) setPlaylistId(id);
        else setResolving(false); // valid response, no clips for this day
      } catch {
        if (!cancelled) {
          setError("fetch");
          setResolving(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [day, apiKey, attempt]);

  useEffect(() => {
    if (!playlistId) return undefined;

    // Guard against a day switch while this query is still in flight: without
    // it, a stale fetchPlaylist promise for a previous day can settle after
    // the day has changed and incorrectly clear `resolving` for the new day.
    let stale = false;
    fetchPlaylist({ variables: { id: playlistId } })
      .then(() => {
        if (!stale) setHydratedId(playlistId);
      })
      .finally(() => {
        if (!stale) setResolving(false);
      });

    return () => {
      stale = true;
    };
  }, [playlistId, fetchPlaylist]);

  const clips: ITodayClip[] = useMemo(() => {
    // Gate on our own record of which id `data` belongs to, so a previous
    // day's cached result never shows while the new day resolves. The
    // response's playlist.id can't be compared to the requested id — the API
    // answers with a base64 global id, not the mongo id it was asked for.
    const list: ITodayClip[] =
      (playlistId && hydratedId === playlistId && data?.playlist?.clips) || [];
    return [...list].sort((a, b) => (b.score || 0) - (a.score || 0));
  }, [data, playlistId, hydratedId]);

  return { clips, error, loading: resolving || loading, retry };
};

export const getClipTagValue = (clip: ITodayClip, key: string) =>
  clip.tags?.find((tag) => tag.key === key)?.value;

export interface IClipPlayerRank {
  rank: number;
  // Valve rank type: 11 Premier, 12 Competitive, 7 Wingman. Null when the
  // clip did not say, in which case the rank reads as a skill group.
  rankType: number | null;
}

// Clip properties arrive as strings, and both backends write "0" for a
// player off Valve matchmaking, so only a positive number is a rank.
const parsePositive = (value?: string) => {
  const parsed = Number(value?.trim() || NaN);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

// The Top 100 job tags each clip with the player's rank at the time of the
// match (`PARTNER_rank` / `PARTNER_rankType`). Older clips carry neither.
export const getClipPlayerRank = (clip: ITodayClip): IClipPlayerRank | null => {
  const rank = parsePositive(getClipTagValue(clip, "PARTNER_rank"));
  if (rank === null) return null;
  return {
    rank,
    rankType: parsePositive(getClipTagValue(clip, "PARTNER_rankType")),
  };
};

// Each clip carries its originating partner as `developer`, whose profile
// is the partner's default ("house") account. A clip owned by that account
// is held on behalf of a player who hasn't claimed it, for any partner.
const isHouseAccount = (clip: ITodayClip) =>
  !!clip.user?._id && clip.user._id === clip.developer?.profile?.internalId;

export interface IClipOwner {
  displayName?: string;
  profilePath: string | null;
}

// A clip with a `user` belongs to an Allstar account — except partner house
// accounts, whose clips are effectively unclaimed. Unclaimed clips show the
// in-game `username` and link to the player's unclaimed profile (which
// carries the claim upsell); with no steamId there is no profile to link.
export const isClaimedClip = (clip: ITodayClip) =>
  !!clip.user && !isHouseAccount(clip);

export const getClipOwner = (clip: ITodayClip): IClipOwner => {
  const claimed = isClaimedClip(clip);

  if (claimed)
    return {
      displayName: clip.user?.username || clip.username,
      profilePath: `/u/${clip.user?.username || clip.user?._id}`,
    };

  return {
    displayName: clip.username,
    profilePath: clip.playerGameIdentifier
      ? `/u/${clip.playerGameIdentifier}`
      : null,
  };
};

export type TFilterGroupKey = "map" | "weapon" | "tag" | "opponent" | "event";

export interface IFilterOption {
  count: number;
  label: string;
  value: string;
}

export interface IFilterGroup {
  key: TFilterGroupKey;
  label: string;
  options: IFilterOption[];
}

// Partial: a page only carries the groups it offers (/today has no opponent).
export type TActiveFilters = Partial<Record<TFilterGroupKey, string | null>>;

const NO_FILTERS: TActiveFilters = { map: null, tag: null, weapon: null };

export interface IFilterGroupConfig {
  key: TFilterGroupKey;
  label: string;
  tagKeys: string[];
}

export const FILTER_GROUPS: IFilterGroupConfig[] = [
  { key: "map", label: "Map", tagKeys: ["CS_Map"] },
  { key: "weapon", label: "Weapon", tagKeys: ["CS_Weapons"] },
  { key: "tag", label: "Tags", tagKeys: ["CS_Situational", "CS_Round Type"] },
];

const clipValuesForGroup = (clip: ITodayClip, tagKeys: string[]) =>
  (clip.tags || [])
    .filter((tag) => tagKeys.includes(tag.key))
    .map((tag) => ({
      label: getTagLabel(tag.key, tag.value),
      value: tag.value.toLowerCase().trim(),
    }));

// `exceptGroup` leaves that group's own filter out of the match, so its
// dropdown keeps offering the alternatives available under the OTHER
// groups' filters (faceted-search behavior).
const clipMatchesFilters = (
  clip: ITodayClip,
  activeFilters: TActiveFilters,
  filterGroups: IFilterGroupConfig[],
  exceptGroup?: TFilterGroupKey,
) =>
  filterGroups.every(({ key, tagKeys }) => {
    if (key === exceptGroup) return true;
    const active = activeFilters[key];
    if (!active) return true;
    return clipValuesForGroup(clip, tagKeys).some(
      (option) => option.value === active,
    );
  });

export const filterClips = (
  clips: ITodayClip[],
  activeFilters: TActiveFilters,
  filterGroups: IFilterGroupConfig[] = FILTER_GROUPS,
) =>
  clips.filter((clip) => clipMatchesFilters(clip, activeFilters, filterGroups));

const labelForValue = (tagKeys: string[], value: string) => {
  for (const key of tagKeys) {
    const label = getTagLabel(key, value);
    if (label !== value.toUpperCase()) return label;
  }
  return value.toUpperCase();
};

export const useClipFilters = (
  clips: ITodayClip[],
  filterGroups: IFilterGroupConfig[] = FILTER_GROUPS,
) => {
  const [activeFilters, setActiveFilters] =
    useState<TActiveFilters>(NO_FILTERS);

  const setFilter = useCallback(
    (group: TFilterGroupKey, value: string | null) =>
      setActiveFilters((prev) => ({ ...prev, [group]: value })),
    [],
  );

  const clearFilters = useCallback(() => setActiveFilters(NO_FILTERS), []);

  const filteredClips = useMemo(
    () => filterClips(clips, activeFilters, filterGroups),
    [clips, activeFilters, filterGroups],
  );

  const groups: IFilterGroup[] = useMemo(
    () =>
      filterGroups.map(({ key, label, tagKeys }) => {
        const counts = new Map<string, IFilterOption>();
        const facetClips = clips.filter((clip) =>
          clipMatchesFilters(clip, activeFilters, filterGroups, key),
        );
        for (const clip of facetClips) {
          const seen = new Set<string>();
          for (const option of clipValuesForGroup(clip, tagKeys)) {
            if (seen.has(option.value)) continue;
            seen.add(option.value);
            const entry = counts.get(option.value);
            if (entry) entry.count += 1;
            else counts.set(option.value, { ...option, count: 1 });
          }
        }

        // An active value can disappear when the day changes or another
        // group's filter excludes it; keep a zero-count option so the
        // viewer can still see and clear it.
        const active = activeFilters[key];
        if (active && !counts.has(active)) {
          const known = clips
            .flatMap((clip) => clipValuesForGroup(clip, tagKeys))
            .find((option) => option.value === active);
          counts.set(active, {
            count: 0,
            label: known?.label ?? labelForValue(tagKeys, active),
            value: active,
          });
        }

        const options = [...counts.values()].sort(
          (a, b) => b.count - a.count || a.label.localeCompare(b.label),
        );
        return { key, label, options };
      }),
    [clips, activeFilters, filterGroups],
  );

  const hasActiveFilters = filterGroups.some(({ key }) => !!activeFilters[key]);

  return {
    activeFilters,
    clearFilters,
    filteredClips,
    groups,
    hasActiveFilters,
    setFilter,
  };
};
